import { z } from 'zod';

/**
 * Tegoed zoals het over de lijn komt. Elk schema volgt één API Resource uit
 * `smartkeur-api` (tussen haakjes); de schermen zien alleen het model uit
 * `model.ts`.
 *
 * Tegoed telt in **hele credits** (getallen). Alleen een tegoedpakket en een
 * betaling hebben een bedrag in euro's, als string met twee decimalen.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** `"450.00"` */
const amount = z.string();

/** (`Billing\CreditWalletResource`) */
export const creditWalletResourceSchema = z.object({
    /** Al netto: een reservering gaat er meteen af. */
    balance: z.number(),
    reserved: z.number(),
    /** `balance + grace_credits`. */
    spendable: z.number(),
    /** Het deel van het saldo dat de abonnementsmaand bracht en aan het eind ervan vervalt. */
    included_balance: z.number().optional(),
    /** De rest: gekocht of gekregen, vervalt nooit. */
    purchased_balance: z.number().optional(),
    included_period_ends_at: timestamp.nullable().optional(),
    low_balance_threshold: z.number(),
    is_low: z.boolean(),
    block_on_empty: z.boolean(),
    grace_credits: z.number(),
});

export type CreditWalletResource = z.infer<typeof creditWalletResourceSchema>;

/** (`Billing\CreditTransactionResource`) één regel uit het grootboek. */
export const creditTransactionResourceSchema = z.object({
    uuid: z.string(),
    type: z.string(),
    type_label: z.string(),
    /** Getekend: `-4` bij een reservering, `50` bij een opwaardering. */
    amount: z.number(),
    balance_after: z.number(),
    description: z.string().nullable(),
    /** De keuring, betaling, factuur of organisatie waar de boeking op staat. */
    reference: z
        .object({
            type: z.string(),
            uuid: z.string().nullable(),
        })
        .nullable(),
    /** Wie boekte; `null` bij een automatische boeking. */
    author: z
        .object({
            uuid: z.string(),
            name: z.string(),
        })
        .nullable(),
    created_at: timestamp.nullable(),
});

export type CreditTransactionResource = z.infer<typeof creditTransactionResourceSchema>;

/** (`Billing\CreditPackageResource`) */
export const creditPackageResourceSchema = z.object({
    uuid: z.string(),
    name: z.string(),
    credits: z.number(),
    /** Exclusief btw. */
    price: amount,
    vat_rate: z.string(),
    price_incl_vat: amount,
    price_per_credit: amount,
});

export type CreditPackageResource = z.infer<typeof creditPackageResourceSchema>;

/** (`Billing\PaymentResource`) een betaling aan Smartkeur die bij de betaalprovider loopt of liep. */
export const paymentResourceSchema = z.object({
    uuid: z.string(),
    provider: z.string(),
    /** `subscription`, `subscription_renewal`, `extra_inspections`, `single_reports` of `credit_package`. */
    purpose: z.string().optional(),
    purpose_label: z.string().optional(),
    description: z.string().nullable().optional(),
    status: z.string(),
    status_label: z.string(),
    /** Incl. btw. */
    amount,
    amount_excl_vat: amount.nullable().optional(),
    vat: amount.nullable().optional(),
    currency: z.string(),
    credits: z.number(),
    quantity: z.number().nullable().optional(),
    plan: z.object({ code: z.string(), name: z.string() }).nullable().optional(),
    period_start: timestamp.nullable().optional(),
    period_end: timestamp.nullable().optional(),
    created_at: timestamp.nullable().optional(),
    checkout_url: z.string().nullable(),
    expires_at: timestamp.nullable(),
    paid_at: timestamp.nullable(),
    /* A subscription or a purchase has no package: the API sends null. */
    package: creditPackageResourceSchema.nullable().optional(),
    /** De factuur van Smartkeur bij een betaalde betaling, zodra die er is. */
    invoice: z
        .object({
            uuid: z.string(),
            number: z.string(),
            issued_at: timestamp,
            pdf_ready: z.boolean(),
        })
        .nullable()
        .optional(),
});

export type PaymentResource = z.infer<typeof paymentResourceSchema>;

const planSummarySchema = z.object({
    code: z.string(),
    name: z.string(),
    monthly_price_cents: z.number(),
    included_inspections: z.number(),
});

/** (`Billing\SubscriptionOverviewResource`) het abonnement bij Smartkeur, `GET /billing/subscription`. */
export const subscriptionOverviewResourceSchema = z.object({
    /** `none`, `active`, `cancelling` of `past_due`. */
    state: z.string(),
    state_label: z.string(),
    subscription: z
        .object({
            uuid: z.string(),
            status: z.string(),
            status_label: z.string(),
            /** `recurring` (automatische incasso) of `link` (maandelijkse betaallink); ontbreekt bij een oudere API. */
            billing_method: z.string().optional(),
            billing_method_label: z.string().optional(),
            plan: planSummarySchema,
            next_plan: planSummarySchema.nullable(),
            started_at: timestamp.nullable(),
            current_period_start: timestamp.nullable(),
            current_period_end: timestamp.nullable(),
            cancelled_at: timestamp.nullable(),
            ends_at: timestamp.nullable(),
            past_due_since: timestamp.nullable(),
        })
        .nullable(),
    next_charge: z
        .object({
            amount,
            amount_excl_vat: amount,
            vat: amount,
            date: timestamp.nullable(),
        })
        .nullable(),
    included: z.object({
        per_month: z.number(),
        left: z.number(),
        expires_at: timestamp.nullable(),
    }),
    purchased_left: z.number(),
    purchase: z.object({
        /** `extra_inspections` (met abonnement) of `single_reports` (zonder). */
        kind: z.string(),
        name: z.string(),
        unit_price_cents: z.number(),
        vat_percent: z.number(),
        min_quantity: z.number(),
        max_quantity: z.number(),
    }),
    plan_preference: z.string().nullable(),
    /** De betaallink van de komende maand, alleen bij `billing_method` `link`. */
    payment_link: z
        .object({
            url: z.string(),
            period: z.string(),
            sends_on: timestamp,
            due: z.boolean(),
        })
        .nullable()
        .optional(),
});

export type SubscriptionOverviewResource = z.infer<typeof subscriptionOverviewResourceSchema>;

/** `GET /billing/payments/{uuid}/invoice`: een tijdelijke link naar de PDF. */
export const invoiceLinkResourceSchema = z.object({
    url: z.string(),
    url_expires_in: z.number(),
    filename: z.string(),
    mime_type: z.string(),
    size_bytes: z.number(),
});
